import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import { NotificationService } from "src/app/shared/components/notification/notification.service";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { environment } from "../../../../environments/environment";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { DemoDataModalComponent } from "../../../shared/components/demo-data-modal/demo-modal.component";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { UserInfo } from "../../../shared/types/user-info";
import { removePropsWithKeys } from "../../../shared/utils/objects/object";
import { AssetService } from "../assets/asset.service";
import { ContractService } from "../contracts/contract.service";
import { PartnerService } from "../partners/partner.service";
import { companyStatuses, getCompanySearchFilterOptions } from "./company";
import { CompanyService } from "./company.service";
import { Company } from "./company.type";

@Injectable({
  providedIn: "root",
})
export class CompanyTableService {
  currentUser: UserInfo;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private companyService: CompanyService,
    private contractService: ContractService,
    private assetService: AssetService,
    private partnerService: PartnerService,
    private dataTableService: DataTableService<Company>,
    private notificationService: NotificationService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  /** Get table config */
  getCompaniesTableConfig = (config?: DataTableConfig): DataTable<Company> => {
    const { hidden, shown, actionColumn, inlineEdit, selection, syncWithQueryParams, entities } = config || {};

    const columns: DataTableColumn<Company>[] = (
      [
        {
          prop: "name",
          name: "Name",
          link: {
            path: (company) => `/companies/${company.id}`,
          },
          inlineEdit: {
            control: "input",
            disabled: (company) => inlineEdit?.disabled?.(company),
            validators: () => [Validators.minLength(2), Validators.maxLength(130)],
          },
        },
        {
          prop: "companyIdentifier",
          name: "Company ID",
        },
        {
          prop: "type",
          name: "Type",
          inlineEdit: {
            control: "input",
            disabled: (company) => inlineEdit?.disabled?.(company),
            validators: () => [Validators.minLength(1), Validators.maxLength(200)],
          },
        },
        {
          prop: "status",
          name: "Status",
          render: (company) => companyStatuses.find((status) => status.value === company.status)?.label,
          inlineEdit: {
            control: "select",
            disabled: (company) => inlineEdit?.disabled?.(company),
            select: {
              options: [...companyStatuses],
            },
          },
        },
        {
          prop: "partner.name",
          name: "Partner",
          hidden: !this.currentUser.isGlobal(),
          link: {
            path: (company) => `/partners/${company.partnerId}`,
          },
        },
      ] as DataTableColumn<Company>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (company) => col?.inlineEdit?.disabled?.(company) || !allowUpdateEntity(company, this.currentUser),
        },
      }));

    return {
      id: "companies",
      entityName: "Company",
      syncWithQueryParams: syncWithQueryParams ?? true,
      toolBar: {
        exportCsv: {
          filename: "companies",
          data: {
            browser: (queryParams) =>
              this.companyService.getCompanies({
                ...queryParams,
                where: {
                  partnerId: entities?.partner?.id,
                  users: { id: entities?.user?.id },
                  ...removePropsWithKeys<Company>(queryParams.where, ["partner.name"]),
                },
                relations: { users: true },
              }),
            email: (queryParams) =>
              this.companyService.getCompaniesQueue({
                ...queryParams,
                where: {
                  partnerId: entities?.partner?.id,
                  users: { id: entities?.user?.id },
                  ...removePropsWithKeys<Company>(queryParams.where, ["partner.name"]),
                },
                relations: { users: true },
              }),
          },
        },
      },
      columns,
      defaultColumns: [],
      selection: {
        ...selection,
        selectedRows: [],
      },
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (company) => this.viewCompany(company, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageCompany,
              hidden: (company) => actionColumn?.view?.hidden,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteCompany,
              hidden: (company) => actionColumn?.delete?.hidden || !allowDeleteEntity(company, this.currentUser),
            },
            {
              icon: "create",
              text: "Create demo data",
              function: this.createDemoData,
              hidden: () => environment.production || !this.currentUser.isGlobalAdmin(),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getCompanySearchFilterOptions(this.currentUser, {
            hidden,
            shown,
            partnerService: this.partnerService,
          }),
        },
      ],
    };
  };

  /** Populate table */
  getCompanies = (event: GetDataEvent<Company>): void => {
    const { onError, onResult, queryParams } = event;
    this.companyService
      .getCompanies({
        relations: { partner: true },
        ...queryParams,
        where: {
          ...removePropsWithKeys<Company>(queryParams.where, ["partner.name"]),
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  getCompaniesPost = (event: GetDataEvent<Company>): void => {
    const { onError, onResult, queryParams } = event;
    this.companyService
      .getCompaniesPost({
        relations: { partner: true },
        ...queryParams,
        where: {
          ...removePropsWithKeys<Company>(queryParams.where, ["partner.name"]),
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Update table */
  update = (event: UpdateDataEvent<Company>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.companyService.updateCompany(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  //#region Private Methods

  private viewCompany = (company: Company, config?: DataTableConfig): void => {
    const columns = this.getCompaniesTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({ name: col.name, value: this.dataTableService.parseData(company, col, get(company, col.prop)) }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private manageCompany = (company: Company): void => {
    this.router.navigate([`/companies/${company.id}`]);
  };

  private deleteCompany = async (company: Company, table: DataTableComponent<Company>): Promise<void> => {
    if (!this.currentUser.isGlobalAdmin()) {
      this.notificationService.error(
        "You do not have access to delete a company. For assistance, please contact portalsupport@nasupport.com",
      );
      return;
    }

    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "company",
        data: company,
        url: this.companyService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private createDemoData = (company: Company, table: DataTableComponent<Company>): void => {
    const dialogRef = this.dialog.open(DemoDataModalComponent, {
      width: "450px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        information: [
          "3 locations",
          "4 contracts",
          "20 assets",
          "4 linked assets per contract",
          "1 ticket with a comment per active contract",
        ],
        name: company.name,
        company: company,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });

    //#endregion
  };

  //#endregion
}
